import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Por favor, intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('access_token');

    // Intenta con el token actual
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        setLoading(false);
        return;
      } catch {
        // Token expirado o inválido — intentar refresh
        localStorage.removeItem('access_token');
      }
    }

    // Intenta refrescar la sesión (usa refresh token en cookie o localStorage)
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      const refreshResponse = await axios.post(
        `${API}/auth/refresh`,
        {},
        {
          withCredentials: true,
          headers: { ...(refreshToken && { Authorization: `Bearer ${refreshToken}` }) }
        }
      );
      // Tras el refresh, obtener el usuario
      if (refreshResponse.data.access_token) {
        localStorage.setItem('access_token', refreshResponse.data.access_token);
      }
      const meResponse = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${refreshResponse.data.access_token || localStorage.getItem('access_token')}`
        }
      });
      setUser(meResponse.data);
    } catch {
      // Refresh también falló — sesión cerrada
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
      }
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      if (!response.data.requires_verification) {
        setUser(response.data);
      }
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(false);
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/auth/google-callback`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
      }
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { ...(token && { Authorization: `Bearer ${token}` }) }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      googleLogin,
      handleGoogleCallback,
      refreshUser,
      isAuthenticated: !!user && user !== false
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
